import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, CreditCard, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const peso = (n) => `\u20B1${Number(n || 0).toLocaleString('en-PH')}`;
const emptyForm = { name: '', credits: 3, price: 0, expiry_days: 0, description: '', is_active: true, regular_class_price: 0 };

const PassManagement = () => {
  const [products, setProducts] = useState([]);
  const [passes, setPasses] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchAll(); }, []);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

  const fetchAll = async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/admin/pass-products`, authHeaders()),
        axios.get(`${API}/admin/class-passes`, authHeaders()),
      ]);
      setProducts(p.data);
      setPasses(c.data);
    } catch (error) {
      console.error('Error fetching passes:', error);
      toast.error('Failed to load class passes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, credits: Number(form.credits) || 1, price: Number(form.price) || 0, expiry_days: Number(form.expiry_days) || 0, regular_class_price: Number(form.regular_class_price) || 0 };
      if (editing) {
        await axios.put(`${API}/pass-products/${editing.id}`, payload, authHeaders());
        toast.success('Pass product updated');
      } else {
        await axios.post(`${API}/pass-products`, payload, authHeaders());
        toast.success('Pass product created');
      }
      fetchAll();
      setIsOpen(false);
      setEditing(null);
      setForm(emptyForm);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pass product');
    }
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, credits: p.credits, price: p.price, expiry_days: p.expiry_days, description: p.description || '', is_active: p.is_active, regular_class_price: p.regular_class_price || 0 });
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pass product? Existing issued passes remain valid.')) return;
    try {
      await axios.delete(`${API}/pass-products/${id}`, authHeaders());
      toast.success('Pass product deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleReverseCredit = async (cp) => {
    if (!window.confirm(`Restore 1 credit to ${cp.code}? Use this to undo a credit deducted by mistake (e.g. a double booking). ${cp.customer_name || ''} will have ${cp.credits_remaining + 1} of ${cp.credits_total} credits.`)) return;
    try {
      const res = await axios.post(`${API}/admin/class-passes/${cp.code}/reverse-credit`, {}, authHeaders());
      toast.success(`Credit restored — ${res.data.credits_remaining} now available`);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse credit');
    }
  };

  const statusBadge = (s) => {
    const map = { active: 'bg-green-100 text-green-700', expired: 'bg-red-100 text-red-600', used_up: 'bg-zinc-200 text-zinc-600' };
    const label = { active: 'Active', expired: 'Expired', used_up: 'Used Up' };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${map[s] || 'bg-zinc-100'}`}>{label[s] || s}</span>;
  };

  return (
    <div className="p-8" data-testid="pass-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <h2 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--heading-font)' }}>
          <CreditCard size={28} /> Class Passes
        </h2>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setIsOpen(true); }} className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2" data-testid="add-pass-product-btn">
          <Plus size={20} /> ADD PASS PRODUCT
        </button>
      </div>

      {/* Products */}
      <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>Pass Products</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {products.length === 0 ? (
          <p className="text-zinc-400 text-sm" data-testid="no-pass-products">No pass products yet. Add one to start selling class passes.</p>
        ) : products.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 p-6 rounded" data-testid={`pass-product-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>{p.name}</h4>
                <p className="text-sm text-zinc-500">{p.credits} credits • {p.expiry_days ? `${p.expiry_days}-day expiry` : 'no expiry'}</p>
              </div>
              {!p.is_active && <span className="text-xs bg-zinc-200 text-zinc-600 px-2 py-0.5 rounded-full">Inactive</span>}
            </div>
            {p.description && <p className="text-sm text-zinc-600 my-2">{p.description}</p>}
            <p className="text-2xl font-bold my-2" style={{ fontFamily: 'var(--heading-font)' }}>{peso(p.price)}</p>
            <p className="text-xs text-zinc-400 mb-4">{peso(p.credits ? p.price / p.credits : 0)} per class</p>
            <div className="flex gap-2">
              <button onClick={() => handleEdit(p)} className="flex-1 bg-zinc-100 hover:bg-zinc-200 rounded px-3 py-2 text-sm font-medium flex items-center justify-center gap-1" data-testid={`edit-pass-product-${p.id}`}><Edit size={14} /> Edit</button>
              <button onClick={() => handleDelete(p.id)} className="flex-1 bg-red-100 hover:bg-red-200 text-red-600 rounded px-3 py-2 text-sm font-medium flex items-center justify-center gap-1" data-testid={`delete-pass-product-${p.id}`}><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Issued passes */}
      <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'var(--heading-font)' }}>Issued Passes ({passes.length})</h3>
      <div className="overflow-x-auto border border-zinc-200 rounded">
        <table className="w-full">
          <thead className="bg-zinc-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-bold">Code</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Product</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Customer</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Credits</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Expiry</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Status</th>
              <th className="px-4 py-3 text-right text-sm font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {passes.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-zinc-400" data-testid="no-issued-passes">No passes issued yet.</td></tr>
            ) : passes.map((cp) => (
              <tr key={cp.id} className="border-t border-zinc-200" data-testid={`issued-pass-${cp.code}`}>
                <td className="px-4 py-3 font-mono font-medium">{cp.code}</td>
                <td className="px-4 py-3 text-sm">{cp.product_name}</td>
                <td className="px-4 py-3 text-sm">
                  <div>{cp.customer_name}</div>
                  <div className="text-zinc-400 text-xs">{cp.customer_email}</div>
                </td>
                <td className="px-4 py-3 text-sm">{cp.credits_remaining} / {cp.credits_total} left</td>
                <td className="px-4 py-3 text-sm text-zinc-600">{cp.expiry_note || cp.expiry_date || 'No expiry'}</td>
                <td className="px-4 py-3">{statusBadge(cp.status)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleReverseCredit(cp)}
                    disabled={(cp.credits_total - cp.credits_remaining) <= 0}
                    className="inline-flex items-center gap-1 bg-zinc-100 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed rounded px-3 py-1.5 text-xs font-medium"
                    title="Restore 1 credit (undo an accidental deduction / double booking)"
                    data-testid={`reverse-credit-${cp.code}`}
                  >
                    <RotateCcw size={13} /> Restore Credit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Product Dialog */}
      <Dialog open={isOpen} onOpenChange={() => { setIsOpen(false); setEditing(null); setForm(emptyForm); }}>
        <DialogContent className="max-w-md" data-testid="pass-product-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              {editing ? 'Edit Pass Product' : 'Add Pass Product'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="pp-name">Pass Name *</Label>
              <Input id="pp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. 5-Class Pass" required data-testid="pass-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pp-credits">Credits (classes) *</Label>
                <Input id="pp-credits" type="number" min="1" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} required data-testid="pass-form-credits" />
              </div>
              <div>
                <Label htmlFor="pp-price">Price (₱) *</Label>
                <Input id="pp-price" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="pass-form-price" />
              </div>
            </div>
            <div>
              <Label htmlFor="pp-expiry">Expiry (days from purchase, 0 = never)</Label>
              <Input id="pp-expiry" type="number" min="0" value={form.expiry_days} onChange={(e) => setForm({ ...form, expiry_days: e.target.value })} data-testid="pass-form-expiry" />
            </div>
            <div>
              <Label htmlFor="pp-regular">Regular Single-Class Price (₱)</Label>
              <Input id="pp-regular" type="number" min="0" value={form.regular_class_price} onChange={(e) => setForm({ ...form, regular_class_price: e.target.value })} placeholder="e.g. 450" data-testid="pass-form-regular-price" />
              <p className="text-xs text-zinc-500 mt-1">Used to show the savings % on the public page. Leave 0 to auto-use your highest class price.</p>
            </div>
            <div>
              <Label htmlFor="pp-desc">Description</Label>
              <Input id="pp-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pass-form-description" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-black" data-testid="pass-form-active" />
              Active (visible for purchase)
            </label>
            <button type="submit" className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95" data-testid="pass-form-submit">
              {editing ? 'UPDATE' : 'CREATE'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PassManagement;
